import { toast } from "react-toastify";
import React, { useMemo, useState } from "react";
import AddSeasonModal from "./AddSeasonModal";
import { GlobalStyles } from "../../constants/styles";
import useCheckLimitOrPrompt from "../common/hooks/useCheckLimitOrPrompt";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";

export default function ActiveSeason({
                                         setGames,
                                         setStages,
                                         activeSeasonData,
                                         seasons,
                                         setSeasons,
                                         setActiveSeasonData,
                                         setTeams,
                                         setStandings,
                                         setSanctions,
                                         division,
                                         readyOldSeason = false,
                                         waivers = [],
                                         colors,
                                         onSeasonChange,
                                         disableAdd = false
                                     }) {
    const [showModal, setShowModal] = useState(false);
    const { t } = useTranslation();

    const styles = useMemo(() => {
        return {
            card: {
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 10px 28px rgba(0,0,0,0.08)",
                background: "#fff",
            },
            header: {
                padding: "14px 14px 12px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.0)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                flexWrap: "wrap",
            },
            titleWrap: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 220,
            },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.04em",
                padding: "6px 10px",
                borderRadius: 999,
                color: colors.textOnPrimary,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                textTransform: "uppercase",
                lineHeight: 1,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontSize: 16,
                fontWeight: 900,
                color: "#14151a",
            },
            sub: {
                margin: "6px 0 0",
                fontSize: 12.5,
                fontWeight: 700,
                color: "rgba(20,21,26,0.60)",
            },
            body: {
                padding: 14,
            },
            label: {
                fontSize: 12.5,
                fontWeight: 800,
                color: "rgba(20,21,26,0.70)",
                marginBottom: 6,
            },
            select: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.12)",
                padding: "0px 12px",
                fontWeight: 800,
                outline: "none",
                backgroundColor: "#fff",
                height: 44,
            },
            hint: {
                marginTop: 8,
                color: "rgba(20,21,26,0.55)",
                fontSize: 12.5,
                fontWeight: 600,
                lineHeight: 1.25,
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.10)",
                background: GlobalStyles.colors.canary,
                color: GlobalStyles.colors.woodsmoke,
                cursor: "pointer",
                whiteSpace: "nowrap",
            },
            btnDisabled: {
                opacity: 0.6,
                cursor: "not-allowed",
            },
        };
    }, [colors.primary, colors.textOnPrimary]);

    const seasonHandler = (seasonSlug) => {
        const activeSeason = seasons?.find((s) => s.slug === seasonSlug);
        if (!activeSeason) return;

        if (onSeasonChange) {
            onSeasonChange(activeSeason);
        } else {
            setActiveSeasonData(activeSeason);
            setStages?.(activeSeason.stages || []);
            setTeams?.(activeSeason.teams || []);

            setGames?.([]);
            setStandings?.([]);
            setSanctions?.([]);
        }
    };

    const checkLimitOrPrompt = useCheckLimitOrPrompt();

    const handleAddSeason = () => {
        const allowed = checkLimitOrPrompt({
            currentCount: seasons?.length || 0,
            itemLabel: "seasons",
            toast,
        });

        if (!allowed) return;
        setShowModal(true);
    };

    const selectValue = activeSeasonData?.slug || "";
    const isFilled = !!selectValue;

    return (
        <>
            <div className="col-md-12 p-0 mt-3 mb-3">
                <div style={styles.card}>
                    <div style={styles.header}>
                        <div>
                            <div style={styles.titleWrap}>
                                <span style={styles.badge}>{t("activeSeason.badge")}</span>
                                <h4 style={styles.title}>{t("activeSeason.title")}</h4>
                            </div>
                            <p style={styles.sub}>{t("activeSeason.subtitle")}</p>
                        </div>

                        {!disableAdd && (
                            <button
                                type="button"
                                onClick={handleAddSeason}
                                style={{ ...styles.btn, ...(readyOldSeason ? styles.btnDisabled : {}) }}
                                disabled={readyOldSeason}
                                title={
                                    readyOldSeason
                                        ? t("activeSeason.lockedTitle")
                                        : t("activeSeason.newSeasonTitle")
                                }
                            >
                                <i className="fas fa-plus" />
                                {t("activeSeason.newSeason")}
                            </button>
                        )}
                    </div>

                    <div style={styles.body}>
                        <label style={styles.label}>{t("activeSeason.activeSeasonLabel")}</label>

                        <select
                            className={`form-control kbola-select ${isFilled ? "kbola-select--filled" : "kbola-select--placeholder"}`}
                            style={styles.select}
                            onChange={(e) => seasonHandler(e.target.value)}
                            disabled={readyOldSeason || !seasons?.length}
                            value={selectValue}
                        >
                            <option value="" disabled>
                                {t("activeSeason.selectSeasonPlaceholder")}
                            </option>

                            {seasons?.map((season) => (
                                <option key={season.slug} value={season.slug}>
                                    {season.name}
                                </option>
                            ))}
                        </select>

                        <div style={styles.hint}>
                            {t("activeSeason.hint")}
                        </div>
                    </div>
                </div>
            </div>

            <AddSeasonModal
                show={showModal}
                onClose={() => setShowModal(false)}
                division={division}
                seasons={seasons}
                setSeasons={setSeasons}
                waivers={waivers}
            />

            <style>{`
        .kbola-select:focus {
          border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.9) !important;
          box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18) !important;
        }

        .kbola-select--placeholder {
          color: rgba(20, 21, 26, 0.55) !important;
        }

        .kbola-select--filled {
          color: rgba(20, 21, 26, 0.92) !important;
        }
      `}</style>
        </>
    );
}